import { miscConstants } from '~/consts.ts';

type AgentPortalPersistedSession = {
  token: string;
  expiresAt: string;
};

const AGENT_PORTAL_MAX_TOKEN_BYTES = 4096;

const isLikelyValidToken = (token: string): boolean => {
  const trimmed = token.trim();
  return trimmed.length > 0 && trimmed.length <= AGENT_PORTAL_MAX_TOKEN_BYTES;
};

const isLikelyFutureDate = (isoDate: string): boolean => {
  const parsed = new Date(isoDate);
  return !Number.isNaN(parsed.getTime()) && parsed.getTime() > Date.now();
};

export const clearAgentPortalAuthSession = (): void => {
  localStorage.removeItem(
    miscConstants.localStorageKeys.getAgentPortalAuthTokenKey()
  );
};

export const persistAgentPortalAuthSession = (
  token: string,
  expiry: Date
): void => {
  if (!isLikelyValidToken(token) || Number.isNaN(expiry.getTime())) {
    clearAgentPortalAuthSession();
    return;
  }

  const payload: AgentPortalPersistedSession = {
    token: token.trim(),
    expiresAt: expiry.toISOString()
  };

  localStorage.setItem(
    miscConstants.localStorageKeys.getAgentPortalAuthTokenKey(),
    JSON.stringify(payload)
  );
};

export const getPersistedAgentPortalAuthToken = (): string | null => {
  const raw = localStorage.getItem(
    miscConstants.localStorageKeys.getAgentPortalAuthTokenKey()
  );
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<AgentPortalPersistedSession>;
    if (
      typeof parsed.token !== 'string' ||
      typeof parsed.expiresAt !== 'string' ||
      !isLikelyValidToken(parsed.token) ||
      !isLikelyFutureDate(parsed.expiresAt)
    ) {
      clearAgentPortalAuthSession();
      return null;
    }
    return parsed.token.trim();
  } catch {
    clearAgentPortalAuthSession();
    return null;
  }
};
